import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf"; // for PDF
import AdminNavbar from "../../components/partial/AdminNavbar";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAdmissionData,
  getAdmissionData,
} from "../../store/admission/reducer";
import { MdDeleteOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FaRegFilePdf } from "react-icons/fa";
import { FaDownload } from "react-icons/fa";

const formatDate = (date) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(date).toLocaleDateString("en-US", options);
};

const getMonthYear = (date) => {
  const options = { year: "numeric", month: "long" };
  return new Date(date).toLocaleDateString("en-US", options);
};

const Admission = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const admissionData = useSelector(
    (state) => state.admissionReducer.admissionData,
  );
  const [admissions, setAdmissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentAdmission, setCurrentAdmission] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const classNames = [
    "PG",
    "LKG",
    "UKG",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
  ];

  useEffect(() => {
    if (!admissionData || admissionData.length === 0) {
      dispatch(getAdmissionData);
    }
  }, [dispatch]);

  useEffect(() => {
    if (admissionData && admissionData.length > 0) {
      const sortedAdmissions = [...admissionData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setAdmissions(sortedAdmissions);
    }
  }, [admissionData]);

  const groupAdmissionsByMonth = () => {
    return admissions.reduce((acc, admission) => {
      const monthYear = getMonthYear(admission.createdAt);
      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      acc[monthYear].push(admission);
      return acc;
    }, {});
  };

  const openModal = (admission) => {
    setCurrentAdmission(admission);
    setShowModal(true);
  };

  const closeModal = () => {
    setCurrentAdmission(null);
    setShowModal(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this admission?")) {
      dispatch(deleteAdmissionData(id)).then(() => {
        dispatch(getAdmissionData);
        setShowModal(false);
      });
    }
  };

  const groupAdmissions = groupAdmissionsByMonth();

  const filteredAdmissions = Object.keys(groupAdmissions).filter(
    (monthYear) => {
      const [month, year] = monthYear.split(" ");
      const monthMatches = selectedMonth ? month === selectedMonth : true;
      const yearMatches = selectedYear ? year === selectedYear : true;
      const admissionsForMonth = groupAdmissions[monthYear];

      const filteredAdmissionsForClass = admissionsForMonth.filter(
        (admission) => {
          const classMatches =
            selectedClass === ""
              ? true
              : admission.personal_details.classEnrolled === selectedClass;
          return monthMatches && yearMatches && classMatches;
        },
      );

      return filteredAdmissionsForClass.length > 0;
    },
  );

  const filteredGroupAdmissions = filteredAdmissions.reduce(
    (acc, monthYear) => {
      const sortedAdmissions = groupAdmissions[monthYear].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      acc[monthYear] = sortedAdmissions;
      return acc;
    },
    {},
  );

  const handleDownloadOption = () => {
    if (currentAdmission) {
      navigate("/nvm-admin/admission/download", {
        state: { currentAdmission },
      });
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center bg-gray-100 min-h-screen">
      <div className="lg:w-1/4 bg-white shadow-md">
        <AdminNavbar />
      </div>

      <div className="flex-1 p-6 md:flex-row">
        <h2 className="text-3xl font-semibold mb-6 text-center">
          Admin Admission Page
        </h2>

        <div className="shadow-md p-4 bg-gray-50 rounded-lg">
          <h3 className="text-2xl font-semibold mb-4 text-center">
            Submitted Admissions
          </h3>

          {/* Date Selection */}
          <div className="md:flex justify-center items-center mb-4 grid grid-rows-3 gap-4">
            <div className="mx-4 text-md rounded-lg border-2 border-[#1e81b0]  ">
              <select
                className="rounded-lg p-2 font-semibold"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">Select Year</option>
                {Object.keys(groupAdmissions).map((key) => {
                  const year = key.split(" ")[1];
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="mx-4 text-md rounded-lg border-2 border-[#1e81b0]  ">
              <select
                className="rounded-lg p-2 font-semibold"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Select Month</option>
                {monthNames.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div className="mx-4 text-md rounded-lg border-2 border-[#1e81b0]  ">
              <select
                className="rounded-lg p-2 font-semibold"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <option value="">Select Class</option>
                {classNames.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {Object.keys(filteredGroupAdmissions).map((monthYear) => (
            <div key={monthYear} className="mb-6">
              <h4 className="text-xl font-bold text-gray-700 mb-2">
                {monthYear}
              </h4>

              <ul className="space-y-3">
                {filteredGroupAdmissions[monthYear].map((admission) => (
                  <li
                    key={admission._id}
                    className="flex justify-between items-center bg-gray-100 rounded-lg p-4 hover:bg-gray-200 cursor-pointer"
                    onClick={() => openModal(admission)}
                  >
                    <div className="flex">
                      <span className="block text-lg font-medium">
                        {admission.personal_details.first_name}{" "}
                        {admission.personal_details.last_name}
                      </span>
                    </div>
                    <div className="text-md font-semibold text-gray-500">
                      {formatDate(admission.createdAt)}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(admission._id);
                      }}
                      className="flex items-center justify-center bg-[#1e81b0] text-white p-2 rounded-md shadow hover:bg-[#1384ba]"
                    >
                      <MdDeleteOutline size={20} />
                      <span className="ml-1 text-sm">Delete</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {showModal && currentAdmission && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-10">
          <div className="bg-white text-black p-8 rounded-lg w-full md:m-5 lg:w-1/3 max-h-[80vh] overflow-y-auto relative">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-3xl font-semibold text-black hover:text-gray-500"
            >
              &times;
            </button>

            <div className=" mb-4 flex items-center justify-start">
              <button
                onClick={handleDownloadOption}
                className="text-[#1e81b0] hover:text-[#1384ba] shadow-lg border-4 border-gray-400 p-2 hover:border-gray-600 rounded-lg flex items-center justify-start"
              >
                <FaDownload className="h-7 w-7" />
              </button>
            </div>

            <h2 className="text-2xl font-semibold mb-4">Admission Details</h2>

            {/* Personal Details Section */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold mb-4 text-center border-b-2 border-black">
                Personal Details
              </h4>
              <div className="mb-2">
                <strong>Student Name: </strong>
                {currentAdmission.personal_details.first_name}{" "}
                {currentAdmission.personal_details.middle_name}{" "}
                {currentAdmission.personal_details.last_name}
              </div>
              <div className="mb-2">
                <strong>Date of Birth: </strong>
                {currentAdmission.personal_details.dob}
              </div>
              <div className="mb-2">
                <strong>Class: </strong>
                {currentAdmission.personal_details.classEnrolled}
              </div>
              <div className="mb-2">
                <strong>Nationality: </strong>
                {currentAdmission.personal_details.nationality}
              </div>
              <div className="mb-2">
                <strong>Address: </strong>
                {currentAdmission.personal_details.address}
              </div>
              <div className="mb-2">
                <strong>Previous School: </strong>
                {currentAdmission.personal_details.previous_school}
              </div>
            </div>

            {/* Father's Details Section */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold mb-4 text-center border-b-2 border-black">
                Father's Details
              </h4>
              <div className="mb-2">
                <strong>Father's Name: </strong>
                {currentAdmission.fathers_details.full_name}
              </div>
              <div className="mb-2">
                <strong>Father's Email: </strong>
                {currentAdmission.fathers_details.email || "N/A"}
              </div>
              <div className="mb-2">
                <strong>Father's Occupation: </strong>
                {currentAdmission.fathers_details.occupation}
              </div>
              <div className="mb-2">
                <strong>Father's Contact: </strong>
                {currentAdmission.fathers_details.number}
              </div>
            </div>

            {/* Mother's Details Section */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold mb-4 text-center border-b-2 border-black">
                Mother's Details
              </h4>
              <div className="mb-2">
                <strong>Mother's Name: </strong>
                {currentAdmission.mothers_details.full_name}
              </div>
              <div className="mb-2">
                <strong>Mother's Email: </strong>
                {currentAdmission.mothers_details.email || "N/A"}
              </div>
              <div className="mb-2">
                <strong>Mother's Occupation: </strong>
                {currentAdmission.mothers_details.occupation}
              </div>
              <div className="mb-2">
                <strong>Mother's Contact: </strong>
                {currentAdmission.mothers_details.number}
              </div>
            </div>

            {/* Guardian's Details Section */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold mb-4 text-center border-b-2 border-black">
                Guardian's Details
              </h4>
              <div className="mb-2">
                <strong>Guardian's Name: </strong>
                {currentAdmission.guardian_details.full_name}
              </div>
              <div className="mb-2">
                <strong>Guardian's Relationship: </strong>
                {currentAdmission.guardian_details.relation}
              </div>
              <div className="mb-2">
                <strong>Guardian's Occupation: </strong>
                {currentAdmission.guardian_details.occupation}
              </div>
              <div className="mb-2">
                <strong>Guardian's Contact: </strong>
                {currentAdmission.guardian_details.number}
              </div>
              <div className="mb-2">
                <strong>Guardian's Address: </strong>
                {currentAdmission.guardian_details.address}
              </div>
            </div>

            <div className="mb-6">
              <strong>Other Details: </strong>
              {currentAdmission.other}
            </div>

            {/* Date Submitted */}
            <div className="mb-4">
              <strong>Date Submitted: </strong>
              {formatDate(currentAdmission.createdAt)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admission;
